import React, { useCallback, useEffect, useState } from 'react';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { WAREHOUSE_RECONCILIATION } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';

const PAGE_SIZE = 50;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const formatDate = (value) => {
  if (!value) return '—';
  const parsed = parseISO(String(value));
  return Number.isNaN(parsed.getTime()) ? value : format(parsed, 'MM/dd/yyyy HH:mm');
};
const formatNumber = (value) => (value === null || value === undefined || value === '' ? '—' : Number(value).toLocaleString());
const percentage = (value, total) => (total ? `${Math.round((value / total) * 100)}%` : '0%');
const severityDot = {
  critical: 'bg-red-600',
  warning: 'bg-amber-500',
  info: 'bg-blue-600',
};
const DETAIL_KEYS = new Set(['section', 'severity', 'location_code', 'area', 'availability_state', 'sku', 'units', 'skus', 'detail']);

const WarehouseReconciliationReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);
  const section = searchParams.get('section') || 'all';
  const [page, setPage] = useState(1);
  const [report, setReport] = useState({ sections: [], totals: {}, data: [], columns: [], totalPages: 1, totalRecords: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const { signal, requestId } = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({ section, page: String(page), pageSize: String(PAGE_SIZE) });
      const response = await fetch(`/api/v1/reports/warehouse-reconciliation?${query}`, {
        headers: authHeaders(),
        signal,
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load warehouse reconciliation report');
      if (!isLatest(requestId)) return;
      setReport(json);
    } catch (requestError) {
      if (requestError?.name === 'AbortError' || !isLatest(requestId)) return;
      setError(requestError.message);
    } finally {
      if (isLatest(requestId)) setLoading(false);
    }
  }, [beginRequest, isLatest, page, section]);

  useEffect(() => {
    if (initialReady) load();
  }, [initialReady, load]);

  useEffect(() => setPage(1), [section]);

  const selectSection = (value) => {
    setPage(1);
    setSearchParams({ section: value });
  };

  const exportData = async () => {
    const query = new URLSearchParams({ section });
    const response = await fetch(`/api/v1/reports/warehouse-reconciliation/export?${query}`, { headers: authHeaders() });
    if (!response.ok) throw new Error('Failed to export warehouse reconciliation report');
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const filename = disposition.match(/filename="?([^"]+)"?/i)?.[1] || 'warehouse-reconciliation.csv';
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    return { handled: true };
  };

  const totals = report.totals || {};
  const selectedSection = section === 'all' ? null : report.sections.find((item) => item.key === section);
  const summaryCards = [
    ['locations', 'Locations in Location Master', totals.locations],
    ['shipheroLinked', 'Linked to ShipHero', `${formatNumber(totals.shipheroLinked)} (${percentage(totals.shipheroLinked, totals.locations)})`],
    ['locationsWithStock', 'Locations holding stock', totals.locationsWithStock],
    ['units', `Units on hand (${formatNumber(totals.skus)} SKUs)`, totals.units],
    ['twinPlaced', 'Placed in 3D twin', `${formatNumber(totals.twinPlaced)} (${percentage(totals.twinPlaced, totals.locations)})`],
    ['twinOutside', 'Outside modelled structure', totals.twinOutside],
    ['twinUnplaced', 'Not placeable', totals.twinUnplaced],
    ['capacityProfiled', 'With a capacity profile', `${formatNumber(totals.capacityProfiled)} (${percentage(totals.capacityProfiled, totals.locations)})`],
  ];

  const renderCell = (row, column) => {
    if (column.key === 'location_code') return row.location_code || '—';
    if (column.key === 'detail') {
      const detail = Object.fromEntries(Object.entries(row).filter(([key]) => !DETAIL_KEYS.has(key)));
      return Object.keys(detail).length ? JSON.stringify(detail) : '—';
    }
    if (['units', 'skus'].includes(column.key) || column.type === 'number') return formatNumber(row[column.key]);
    if (['lastUpdate', 'shiphero_synced_at', 'shiphero_missing_since'].includes(column.key)) return formatDate(row[column.key]);
    if (typeof row[column.key] === 'boolean') return row[column.key] ? 'Yes' : 'No';
    return row[column.key] ?? '—';
  };

  const pagination = (
    <div className="flex items-center justify-between text-sm">
      <span>{formatNumber(report.totalRecords)} rows</span>
      <div className="flex gap-2">
        <button type="button" disabled={report.page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1 disabled:opacity-50">Previous</button>
        <span className="py-1">Page {report.page || page} of {report.totalPages || 1}</span>
        <button type="button" disabled={report.page >= report.totalPages} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1 disabled:opacity-50">Next</button>
      </div>
    </div>
  );

  return (
    <ReportTemplate
      reportId={WAREHOUSE_RECONCILIATION}
      title="Warehouse Location Reconciliation"
      description="Tie out ShipHero locations and on-hand inventory against the Location Master, the 3D twin and capacity profiles. Read-only."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={loading}
      error={error}
      exportButton={<CSVExportButton data={report.data} columns={report.columns} filename="warehouse-reconciliation" onExport={exportData} />}
      pagination={pagination}
      renderChildrenOnly
    >
      <div className="space-y-4">
        <div className={`text-sm ${report.sections.find((item) => item.key === 'sync_stale')?.count > 0 ? 'text-red-600' : 'text-gray-600'}`}>
          ShipHero sync: {report.shipheroSync?.status || 'Unknown'} · last run {formatDate(report.shipheroSync?.lastUpdate)} ({Math.round(report.shipheroSync?.ageHours || 0)}h ago)
        </div>
        <div className="grid gap-3 md:grid-cols-4">
          {summaryCards.map(([key, label, value]) => <div key={key} className="rounded border bg-white p-3"><div className="text-xs text-gray-500">{label}</div><div className="mt-1 font-semibold">{typeof value === 'string' ? value : formatNumber(value)}</div></div>)}
        </div>
        <div className="rounded border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-950">
          <strong>Capacity vs. location.</strong> “No capacity profile” means Eadin knows the location but not how much fits in it. It is not a missing location and not "unlimited". Until a profile is assigned, KIP will say capacity is unknown and ask for manual confirmation before recommending a move into it.
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => selectSection('all')} className={`rounded-full px-3 py-1.5 text-xs ${section === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`}>All findings</button>
          {report.sections.map((item) => {
            const dot = item.count > 0 ? severityDot[item.severity] || 'bg-gray-500' : 'bg-green-600';
            return <button type="button" key={item.key} onClick={() => selectSection(item.key)} className={`rounded-full px-3 py-1.5 text-xs ${section === item.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`}><span className={`mr-1.5 inline-block h-1.5 w-1.5 rounded-full ${dot}`} />{item.title} · {formatNumber(item.count)}{item.units > 0 ? ` · ${formatNumber(item.units)} units` : ''}{item.count === 0 ? ' · OK' : ''}</button>;
          })}
        </div>
        {selectedSection && <div className="text-xs text-gray-600">{selectedSection.description}
          {selectedSection.key === 'capacity_missing' && <div className="mt-2 flex flex-wrap gap-2">{(selectedSection.byArea || []).map((item) => <span key={item.area} className="rounded bg-gray-100 px-2 py-1">{item.area}: {formatNumber(item.count)} ({formatNumber(item.withStock)} with stock)</span>)}</div>}
          {selectedSection.key === 'stock_in_unavailable' && <div className="mt-2 flex flex-wrap gap-2">{(selectedSection.byState || []).map((item) => <span key={item.availability_state} className="rounded bg-gray-100 px-2 py-1">{item.availability_state}: {formatNumber(item.locations)} locations · {formatNumber(item.units)} units</span>)}</div>}
        </div>}
        <div className="overflow-auto rounded border bg-white">
          {report.data.length === 0 ? <div className="px-6 py-12 text-center text-sm text-gray-500">No findings in this section.</div> : <table className="min-w-max text-left text-xs"><thead className="sticky top-0 bg-gray-100"><tr>{report.columns.map((column) => <th key={column.key} className="whitespace-nowrap px-3 py-2">{column.label}</th>)}</tr></thead><tbody>{report.data.map((row, index) => <tr key={`${row.location_code || row.section}-${index}`} className={index % 2 ? 'bg-gray-50' : ''}>{report.columns.map((column) => <td key={column.key} className="whitespace-nowrap px-3 py-2">{renderCell(row, column)}</td>)}</tr>)}</tbody></table>}
        </div>
      </div>
    </ReportTemplate>
  );
};

export default WarehouseReconciliationReport;
